<script>
import { preventDefault } from "svelte/legacy";
import { onMount } from "svelte";
import { projectState } from "@app/store/projectStore.svelte.js";
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import {
    styleState,
    selectPreset,
    initializePresets,
    savePreset as savePresetToStore,
    deletePreset as deletePresetFromStore,
} from "@app/store/styleStore.svelte.js";
import { modalState, hideModal } from "@app/store/modalStore.svelte.js";
import { toast } from "@zerodevx/svelte-toast";
import { authState } from "@app/store/authStore.svelte.js";
import { shouldWatermark } from "@app/lib/entitlements.js";
import { warnIfWatermarked } from "@app/lib/upgradePrompt.js";
import { fontState } from "@app/store/fontStore.svelte.js";
import { fade } from "svelte/transition";
import Swal from "sweetalert2";
import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
import { activateSubscription } from "@app/lib/activateSubscription.js";
import { v4 as uuidv4 } from "uuid";
import { BarLoader } from "svelte-loading-spinners";
import * as m from "@app/paraglide/messages.js";

/* CC LIB */
import encode from "@cclib/functions/encode.js";
import defaults from "@cclib/lib/defaults.js";
import tcLib from "@cclib/lib/timecode.js";
import _Event from "@cclib/classes/event.js";
import _EventGroup from "@cclib/classes/eventGroup.js";
import orderByTime from "@cclib/functions/eventGroups/orderByTime.js";
import removeHtmlEntities from "@cclib/functions/eventGroups/removeHtmlEntities.js";
import reorderRtlText from "@cclib/functions/utility/reorderRtlText.js";
import wrapStyledTextWithSpan from "@cclib/functions/quill/wrapStyledTextWithSpan.js";
import replaceTrialText from "@cclib/functions/eventGroups/replaceTrialText.js";
import {
    generateFcpXml,
    getFcpXmlFileName,
    getImageFileName,
} from "@app/lib/imageExport/generateFcpXml.js";
import {
    ensureScriptExtension,
    generateSst,
    getScenaristSdResolution,
    resolveScenaristPlanes,
    rgbToHex,
} from "@app/lib/imageExport/generateSst.js";
import {
    encodeRgbTiffPackBits,
    quantizeDvdPlanes,
    rgb24ToRgba,
} from "@app/lib/imageExport/scenaristTiff.js";

/* Electron/FFMPEG */
const { shell } = window;
const ipcRenderer = window.ipcRenderer;
const execFile = window.execFile;
const os = window.os;
const path = window.path;

// Get ffmpeg path (unpacked from asar)
const ffmpegPath = window.ffmpegPath.replace("app.asar", "app.asar.unpacked");

// Resolution presets for image export
const resolutionPresets = [
    { id: "ntsc_sd", name: "NTSC SD (720x480)", width: 720, height: 480 },
    { id: "pal_sd", name: "PAL SD (720x576)", width: 720, height: 576 },
    { id: "hd_720", name: "HD 720p (1280x720)", width: 1280, height: 720 },
    { id: "hd_1080", name: "HD 1080p (1920x1080)", width: 1920, height: 1080 },
    { id: "hd_1080i", name: "HD 1080i (1920x1080)", width: 1920, height: 1080 },
    { id: "uhd_4k", name: "UHD 4K (3840x2160)", width: 3840, height: 2160 },
    { id: "dci_2k", name: "DCI 2K (2048x1080)", width: 2048, height: 1080 },
    { id: "dci_4k", name: "DCI 4K (4096x2160)", width: 4096, height: 2160 },
];

// State variables
let targetFolderPath = $state(sessionStorage.getItem("imageExportPath"));
let selectedResolution = $state(
    localStorage.getItem("cc-image-export-resolution") || "hd_1080",
);
let scriptName = $state(
    localStorage.getItem("cc-image-export-script-name") || "untitled.xml",
);
let imagePrefix = $state(
    localStorage.getItem("cc-image-export-prefix") || "DVD",
);
let descriptionText = $state(
    localStorage.getItem("cc-image-export-description") || "",
);
let languageCode = $state(
    localStorage.getItem("cc-image-export-language") || "eng",
);
let exportProfile = $state(
    localStorage.getItem("cc-image-export-profile") === "scenarist"
        ? "scenarist"
        : "bdn",
);

// Background settings
let useTransparentBackground = $state(
    localStorage.getItem("cc-image-export-transparent") !== "false",
);
let backgroundColor = $state(
    localStorage.getItem("cc-image-export-bg-color") || "#00FF00",
);

let progress = $state(0);
let exporting = $state(false);
let eventGroup = $state(eventGroupState[projectState.selected].id);
let statusMsg = $state("");
let tempDir = os.tmpdir();

// Style-related state
let showStylePanel = $state(false);
let previewImageSrc = $state("");
let generatingPreview = $state(false);
let selectedEventIndex = $state(0);
let showMagnifiedPreview = $state(false);

// Derived values
let isScenarist = $derived(exportProfile === "scenarist");
let availableResolutions = $derived(
    isScenarist
        ? resolutionPresets.filter(
              (preset) => preset.id === "ntsc_sd" || preset.id === "pal_sd",
          )
        : resolutionPresets,
);
let currentResolution = $derived(
    resolutionPresets.find((r) => r.id === selectedResolution) ||
        resolutionPresets[3],
);
let totalEvents = $derived(
    eventGroupState.find((g) => g.id === eventGroup)?.events?.length || 0,
);
let imageNaming = $derived(
    isScenarist
        ? { extension: "tif", separator: "." }
        : { extension: "png", separator: "" },
);

// Show trial warning if applicable
warnIfWatermarked("image", showTrialWarning);

function showTrialWarning() {
    Swal.fire({
        ...SWAL_BASE,
        titleText: m.image_export_trial_title(),
        text: m.image_export_trial_text(),
        showDenyButton: true,
        showCancelButton: false,
        confirmButtonText: m.image_export_trial_activate(),
        denyButtonText: m.image_export_trial_continue(),
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: SWAL_CLASSES.trial,
    })
        .then((result) => {
            if (result.isConfirmed) {
                activateSubscription();
            }
        })
        .catch((err) => {
            console.log(err);
            console.log(err.message);
        });
}

function selectTargetFolder() {
    ipcRenderer
        .invoke("selectFolder", {
            title: m.image_export_select_folder_title(),
            properties: ["openDirectory", "createDirectory"],
        })
        .then((result) => {
            if (!result.canceled) {
                targetFolderPath = result.filePaths[0];
                sessionStorage.setItem("imageExportPath", targetFolderPath);
            }
        });
}

// Save settings to localStorage
function saveSettings() {
    localStorage.setItem("cc-image-export-profile", exportProfile);
    localStorage.setItem("cc-image-export-resolution", selectedResolution);
    localStorage.setItem("cc-image-export-script-name", scriptName);
    localStorage.setItem("cc-image-export-prefix", imagePrefix);
    localStorage.setItem("cc-image-export-description", descriptionText);
    localStorage.setItem("cc-image-export-language", languageCode);
    localStorage.setItem(
        "cc-image-export-transparent",
        useTransparentBackground.toString(),
    );
    localStorage.setItem("cc-image-export-bg-color", backgroundColor);
}

function applyProfileConstraints() {
    if (exportProfile === "scenarist") {
        scriptName = ensureScriptExtension(scriptName, "sst");
        useTransparentBackground = false;
        selectedResolution = getScenaristSdResolution(
            selectedResolution,
            projectState.frameRate,
        );
    } else {
        scriptName = ensureScriptExtension(scriptName, "xml");
    }
}

function onProfileChange() {
    applyProfileConstraints();
}

function runFfmpeg(args) {
    return new Promise((resolve, reject) => {
        execFile(ffmpegPath, args, (error, stdout, stderr) => {
            if (error) {
                console.error("FFmpeg error:", error);
                console.error("FFmpeg stderr:", stderr);
                reject(error);
                return;
            }
            resolve({ stdout, stderr });
        });
    });
}

function formatAssPathForFfmpeg(subtitleFilePath) {
    let formattedSubPath =
        subtitleFilePath.substring(1, 2) === ":"
            ? subtitleFilePath.substring(2)
            : subtitleFilePath;
    return formattedSubPath.replace(/\\/g, "/");
}

function getScenaristStyleOverrides() {
    const planes = resolveScenaristPlanes(
        styleState.color,
        styleState.outlineColor,
    );
    return {
        planes,
        color: rgbToHex(planes.PA),
        outlineColor: rgbToHex(planes.E1),
        shadow: 0,
    };
}

function asRgb24(buffer, width, height) {
    const expected = width * height * 3;
    const bytes =
        buffer instanceof Uint8Array ? buffer : Uint8Array.from(buffer);
    if (bytes.length < expected) {
        throw new Error(
            `RGB frame too small: ${bytes.length} < ${expected}`,
        );
    }
    return bytes.subarray(0, expected);
}

function rgbBufferToPreviewSrc(rgb, width, height) {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    const imageData = new ImageData(rgb24ToRgba(rgb, width, height), width, height);
    ctx.putImageData(imageData, 0, 0);
    return canvas.toDataURL("image/png");
}

function formatExportTc(seconds) {
    return tcLib.secToTc(
        seconds,
        projectState.frameRate || 25,
        projectState.dropFrame,
    );
}

// Generate BDN XML script content
function generateBdnXml(events, resolution) {
    const videoFormatMap = {
        ntsc_sd: "480i",
        pal_sd: "576i",
        hd_720: "720p",
        hd_1080: "1080p",
        hd_1080i: "1080i",
        uhd_4k: "2160p",
        dci_2k: "2048p",
        dci_4k: "4096p",
    };

    const videoFormat = videoFormatMap[selectedResolution] || "1080p";
    const frameRate = projectState.frameRate || 25;
    const dropFrame = projectState.dropFrame ? "true" : "false";

    // Get first and last event timecodes
    const firstEvent = events[0];
    const lastEvent = events[events.length - 1];
    const firstInTC = tcLib.secToTc(
        firstEvent.start,
        frameRate,
        projectState.dropFrame,
    );
    const lastOutTC = tcLib.secToTc(
        lastEvent.end,
        frameRate,
        projectState.dropFrame,
    );

    let xmlContent = `<?xml version="1.0" encoding="UTF-8"?>
<BDN Version="0.93" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:noNamespaceSchemaLocation="BD-03-006-0093b BDN File Format.xsd">
	<Description>
		<Name Title="${descriptionText || m.image_export_default_title()}" Content=""/>
\t\t<Language Code="${languageCode}"/>
\t\t<Format VideoFormat="${videoFormat}" FrameRate="${frameRate}" DropFrame="${dropFrame}"/>
\t\t<Events LastEventOutTC="${lastOutTC}" FirstEventInTC="${firstInTC}" ContentInTC="${firstInTC}" ContentOutTC="${lastOutTC}" NumberofEvents="${events.length}" Type="Graphic"/>
\t</Description>
\t<Events>
`;

    events.forEach((event, index) => {
        const inTC = tcLib.secToTc(
            event.start,
            frameRate,
            projectState.dropFrame,
        );
        const outTC = tcLib.secToTc(
            event.end,
            frameRate,
            projectState.dropFrame,
        );
        const imageFileName = getImageFileName(imagePrefix, index);

        xmlContent += `\t\t<Event Forced="false" InTC="${inTC}" OutTC="${outTC}">
\t\t\t<Graphic Width="${resolution.width}" Height="${resolution.height}" X="0" Y="0">${imageFileName}</Graphic>
\t\t</Event>
`;
    });

    xmlContent += `\t</Events>
</BDN>`;

    return xmlContent;
}

// Generate ASS subtitle content for a single event
async function generateAssForEvent(event, resolution, styleOverrides = {}) {
    let copyOfEvent = JSON.parse(JSON.stringify(event));
    copyOfEvent.start = 0;
    copyOfEvent.end = 5;

    let copyOfEventGroup = { events: [copyOfEvent] };

    if (eventGroupState.find((g) => g.id === eventGroup)?.rtl) {
        copyOfEventGroup = reorderRtlText(copyOfEventGroup);
    }

    let xPadding = styleState.xPadding;
    let yPadding = styleState.yPadding;
    const fontColor = styleOverrides.color ?? styleState.color;
    const outlineColor = styleOverrides.outlineColor ?? styleState.outlineColor;
    const shadow = styleOverrides.shadow ?? styleState.shadow;

    let options = new defaults.options({
        profile: "subStationAlpha",
        formatOptions: [
            {
                name: "Border Style",
                selected: styleState.borderStyle,
            },
            {
                name: "Font Family",
                selected: styleState.fontFamily,
            },
            {
                name: "Font Size",
                selected: styleState.fontSize,
            },
            {
                name: "Font Color",
                selected: fontColor,
            },
            {
                name: "Opacity",
                selected: styleState.opacity,
            },
            {
                name: "Letter Spacing",
                selected: styleState.spacing,
            },
            {
                name: "Shadow Size (px)",
                selected: shadow,
            },

            {
                name: "Shadow Color",
                selected: styleState.shadowColor,
            },
            {
                name: "Shadow Opacity",
                selected: styleState.shadowOpacity,
            },
            {
                name: "Outline Size (px)",
                selected: styleState.outline,
            },

            {
                name: "Outline Color",
                selected: outlineColor,
            },
            {
                name: "Outline Opacity",
                selected: styleState.outlineOpacity,
            },
            { name: "Video Width", selected: resolution.width },
            { name: "Video Height", selected: resolution.height },
            { name: "ScaleX", selected: styleState.scaleX || 100 },
            { name: "ScaleY", selected: styleState.scaleY || 100 },
            {
                name: "Margin X",
                selected: parseInt(resolution.width) * (xPadding / 100),
            },
            {
                name: "Margin Y",
                selected: parseInt(resolution.height) * (yPadding / 100),
            },
        ],
        frameRate: projectState.frameRate,
        dropFrame: projectState.dropFrame,
        incode: "00:00:00:00",
        window: {
            width: styleState.width,
            height: styleState.height,
            xPadding: styleState.xPadding,
            yPadding: styleState.yPadding,
        },
    });

    let newEventGroup = {
        ...new _EventGroup({ events: copyOfEventGroup.events }),
    };
    newEventGroup = orderByTime(newEventGroup);
    newEventGroup = removeHtmlEntities(newEventGroup);

    newEventGroup.events.forEach((ev, index, events) => {
        events[index].text = wrapStyledTextWithSpan(ev.text);
    });

    return await encode(newEventGroup, options);
}

async function renderEventToRawRgb(event, resolution, styleOverrides = {}) {
    const stamp = `${Date.now()}_${Math.random().toString(16).slice(2)}`;
    const subtitleFilePath = path.join(tempDir, `scenarist_${stamp}.ass`);
    const rawPath = path.join(tempDir, `scenarist_${stamp}.rgb`);
    const subtitleContents = await generateAssForEvent(
        event,
        resolution,
        styleOverrides,
    );
    await window.fs.writeFile(subtitleFilePath, subtitleContents);
    const formattedSubPath = formatAssPathForFfmpeg(subtitleFilePath);
    const args = [
        "-f",
        "lavfi",
        "-i",
        `color=c=0xFFFFFF:s=${resolution.width}x${resolution.height}`,
        "-vf",
        `ass='${formattedSubPath}'`,
        "-frames:v",
        "1",
        "-f",
        "rawvideo",
        "-pix_fmt",
        "rgb24",
        "-y",
        rawPath,
    ];
    try {
        await runFfmpeg(args);
        return await window.fs.readFile(rawPath);
    } finally {
        await window.fs.unlink(subtitleFilePath).catch(console.error);
        await window.fs.unlink(rawPath).catch(console.error);
    }
}

async function renderBdnPng(event, resolution, outputPath) {
    const subtitleFilePath = path.join(
        tempDir,
        `export_${Date.now()}_${Math.random().toString(16).slice(2)}.ass`,
    );
    const subtitleContents = await generateAssForEvent(event, resolution);
    await window.fs.writeFile(subtitleFilePath, subtitleContents);
    const formattedSubPath = formatAssPathForFfmpeg(subtitleFilePath);
    let args;
    if (useTransparentBackground) {
        args = [
            "-f",
            "lavfi",
            "-i",
            `color=c=0x00FF00:s=${resolution.width}x${resolution.height}`,
            "-vf",
            `ass='${formattedSubPath}',colorkey=0x00FF00:similarity=0.2:blend=0.3,despill=green`,
            "-frames:v",
            "1",
            "-y",
            outputPath,
        ];
    } else {
        const bgHex = backgroundColor.replace("#", "");
        args = [
            "-f",
            "lavfi",
            "-i",
            `color=c=0x${bgHex}:s=${resolution.width}x${resolution.height}`,
            "-vf",
            `ass='${formattedSubPath}'`,
            "-frames:v",
            "1",
            "-y",
            outputPath,
        ];
    }
    try {
        await runFfmpeg(args);
    } finally {
        await window.fs.unlink(subtitleFilePath).catch(console.error);
    }
}

// Generate preview image
async function generatePreview() {
    if (generatingPreview) return;

    generatingPreview = true;
    previewImageSrc = "";

    try {
        let currentEventGroup = eventGroupState.find(
            (group) => group.id === eventGroup,
        );
        if (!currentEventGroup || !currentEventGroup.events.length) {
            toast.push(m.image_export_preview_no_events(), {
                classes: ["toast-warning"],
            });
            return;
        }

        let eventToPreview = currentEventGroup.events[selectedEventIndex];
        if (!eventToPreview) {
            toast.push(m.image_export_preview_no_event_selected(), {
                classes: ["toast-warning"],
            });
            return;
        }

        if (shouldWatermark("image")) {
            eventToPreview = JSON.parse(JSON.stringify(eventToPreview));
            eventToPreview.text = eventToPreview.text.replace(
                /\b\w+\b/g,
                "TRIAL",
            );
        }

        if (isScenarist) {
            const styleOverrides = getScenaristStyleOverrides();
            const rgb = asRgb24(
                await renderEventToRawRgb(
                    eventToPreview,
                    currentResolution,
                    styleOverrides,
                ),
                currentResolution.width,
                currentResolution.height,
            );
            const quantized = quantizeDvdPlanes(rgb, styleOverrides.planes);
            previewImageSrc = rgbBufferToPreviewSrc(
                quantized,
                currentResolution.width,
                currentResolution.height,
            );
            return;
        }

        const previewOutputPath = path.join(tempDir, `preview_${Date.now()}.png`);
        await renderBdnPng(eventToPreview, currentResolution, previewOutputPath);
        const buffer = await window.fs.readFile(previewOutputPath);
        previewImageSrc = `data:image/png;base64,${buffer.toString("base64")}`;
        await window.fs.unlink(previewOutputPath).catch(console.error);
    } catch (error) {
        console.error("Preview error:", error);
        toast.push(m.image_export_preview_failed({ error: error.message }), {
            classes: ["toast-danger"],
        });
    } finally {
        generatingPreview = false;
    }
}

// Export all images
async function exportImages() {
    if (!targetFolderPath) {
        toast.push(m.image_export_no_folder_selected_toast(), {
            classes: ["toast-warning"],
        });
        return;
    }

    applyProfileConstraints();
    saveSettings();
    exporting = true;
    progress = 0;

    try {
        toast.push(m.image_export_starting_toast(), { classes: ["toast-info"] });

        let currentEventGroup = eventGroupState.find(
            (group) => group.id === eventGroup,
        );
        if (!currentEventGroup || !currentEventGroup.events.length) {
            toast.push(m.image_export_no_events_to_export(), {
                classes: ["toast-warning"],
            });
            exporting = false;
            return;
        }

        let copyOfEventGroup = JSON.parse(JSON.stringify(currentEventGroup));

        if (copyOfEventGroup.rtl) {
            copyOfEventGroup = reorderRtlText(copyOfEventGroup);
        }

        copyOfEventGroup = orderByTime(copyOfEventGroup);
        copyOfEventGroup = removeHtmlEntities(copyOfEventGroup);

        if (shouldWatermark("image")) {
            copyOfEventGroup = replaceTrialText(copyOfEventGroup);
        }

        const events = copyOfEventGroup.events;
        const totalCount = events.length;
        const scenaristOverrides = isScenarist
            ? getScenaristStyleOverrides()
            : null;
        const outputScriptName = ensureScriptExtension(
            scriptName,
            isScenarist ? "sst" : "xml",
        );

        for (let i = 0; i < totalCount; i++) {
            statusMsg = m.image_export_status_exporting_image({
                current: i + 1,
                total: totalCount,
            });
            progress = ((i / totalCount) * 100).toFixed(1);

            const event = events[i];
            const imageFileName = getImageFileName(imagePrefix, i, imageNaming);
            const outputPath = path.join(targetFolderPath, imageFileName);

            if (isScenarist) {
                const rgb = asRgb24(
                    await renderEventToRawRgb(
                        event,
                        currentResolution,
                        scenaristOverrides,
                    ),
                    currentResolution.width,
                    currentResolution.height,
                );
                const quantized = quantizeDvdPlanes(
                    rgb,
                    scenaristOverrides.planes,
                );
                const tiff = encodeRgbTiffPackBits(
                    quantized,
                    currentResolution.width,
                    currentResolution.height,
                );
                await window.fs.writeFile(outputPath, tiff);
            } else {
                await renderBdnPng(event, currentResolution, outputPath);
            }
        }

        statusMsg = isScenarist
            ? m.image_export_status_generating_sst()
            : m.image_export_status_generating_xml();
        const scriptPath = path.join(targetFolderPath, outputScriptName);

        if (isScenarist) {
            const sstBuffer = generateSst({
                events,
                scriptName: outputScriptName,
                imagePrefix,
                targetFolderPath,
                resolutionId: selectedResolution,
                dropFrame: projectState.dropFrame,
                formatTc: formatExportTc,
                incode: projectState.incode || 0,
                fillColor: styleState.color,
                outlineColor: styleState.outlineColor,
            });
            await window.fs.writeFile(scriptPath, sstBuffer);
        } else {
            const xmlContent = generateBdnXml(events, currentResolution);
            await window.fs.writeFile(scriptPath, xmlContent);
        }

        const fcpXmlContent = generateFcpXml({
            events,
            resolution: currentResolution,
            targetFolderPath,
            imagePrefix,
            sequenceName: descriptionText || m.image_export_default_title(),
            frameRate: projectState.frameRate || 25,
            dropFrame: projectState.dropFrame,
            useTransparentBackground: isScenarist
                ? false
                : useTransparentBackground,
            resolutionId: selectedResolution,
            imageExtension: imageNaming.extension,
            imageSeparator: imageNaming.separator,
        });
        const fcpXmlPath = path.join(
            targetFolderPath,
            getFcpXmlFileName(outputScriptName),
        );
        await window.fs.writeFile(fcpXmlPath, fcpXmlContent);

        progress = 100;
        statusMsg = m.image_export_status_export_complete();
        toast.push(
            isScenarist
                ? m.image_export_success_toast_scenarist({ count: totalCount })
                : m.image_export_success_toast({ count: totalCount }),
            {
                classes: ["toast-success"],
            },
        );
        shell.showItemInFolder(scriptPath);
        hideModal();
    } catch (error) {
        console.error("Export error:", error);
        toast.push(m.image_export_failed_toast({ error: error.message }), {
            classes: ["toast-danger"],
        });
        statusMsg = m.image_export_status_export_failed({ error: error.message });
    } finally {
        exporting = false;
    }
}

// Navigation functions for preview
function goToStart() {
    selectedEventIndex = 0;
    generatePreview();
}

function goToPrevious() {
    if (selectedEventIndex > 0) {
        selectedEventIndex = selectedEventIndex - 1;
        generatePreview();
    }
}

function goToNext() {
    if (selectedEventIndex < totalEvents - 1) {
        selectedEventIndex = selectedEventIndex + 1;
        generatePreview();
    }
}

function goToEnd() {
    selectedEventIndex = totalEvents - 1;
    generatePreview();
}

// Preset management functions
function savePreset() {
    Swal.fire({
        ...SWAL_BASE,
        titleText: m.image_export_style_preset_save_title(),
        text: m.image_export_style_preset_save_text(),
        showDenyButton: true,
        showCancelButton: false,
        confirmButtonText: m.image_export_style_preset_save_confirm(),
        denyButtonText: m.image_export_style_preset_save_cancel(),
        allowOutsideClick: false,
        allowEscapeKey: false,
        inputLabel: m.image_export_style_preset_name_label(),
        inputValue: styleState.selectedPreset?.name || m.image_export_style_preset_default_name(),
        input: "text",
        customClass: SWAL_CLASSES.denyCancel,
    })
        .then(async (result) => {
            if (result.isConfirmed) {
                const presetName = result.value;
                if (presetName) {
                    try {
                        await savePresetToStore(presetName, uuidv4);
                        toast.push(m.image_export_style_preset_saved(), {
                            classes: ["toast-success"],
                        });
                    } catch (error) {
                        console.error("Error saving preset:", error);
                        toast.push(error.message, {
                            classes: ["toast-danger"],
                        });
                    }
                }
            }
        })
        .catch((err) => {
            console.error(err);
            toast.push(err.message, { classes: ["toast-danger"] });
        });
}

function deletePreset() {
    if (!styleState.selectedPreset) {
        toast.push(m.image_export_style_preset_none_selected(), { classes: ["toast-warning"] });
        return;
    }

    Swal.fire({
        ...SWAL_BASE,
        titleText: m.image_export_style_preset_delete_title(),
        text: m.image_export_style_preset_delete_text({ name: styleState.selectedPreset.name }),
        showDenyButton: true,
        showCancelButton: false,
        confirmButtonText: m.image_export_style_preset_delete_confirm(),
        denyButtonText: m.image_export_style_preset_delete_cancel(),
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: SWAL_CLASSES.destructiveDeny,
    })
        .then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await deletePresetFromStore();
                    toast.push(m.image_export_style_preset_deleted(), {
                        classes: ["toast-success"],
                    });
                } catch (error) {
                    console.error("Error deleting preset:", error);
                    toast.push(m.image_export_style_preset_delete_failed({ error: error.message }), {
                        classes: ["toast-danger"],
                    });
                }
            }
        })
        .catch((err) => {
            console.error(err);
            toast.push(err.message, { classes: ["toast-danger"] });
        });
}

// Download preview
function downloadPreview() {
    if (!previewImageSrc) return;

    try {
        const base64Data = previewImageSrc.split(",")[1];
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "image/png" });

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${getImageFileName(imagePrefix, selectedEventIndex, imageNaming).replace(/\.(png|tif)$/i, "")}_preview.png`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);

        toast.push(m.image_export_preview_downloaded(), {
            classes: ["toast-success"],
        });
    } catch (error) {
        console.error("Download error:", error);
        toast.push(m.image_export_preview_download_failed({ error: error.message }), {
            classes: ["toast-danger"],
        });
    }
}

// Initialize presets when modal opens
onMount(() => {
    applyProfileConstraints();
    if (styleState.presets.length === 0) {
        initializePresets();
    }
});
</script>

<div
    transition:fade={{ duration: 100 }}
    class="modal {modalState.selected === 'imageExport' ? 'show d-block' : ''}"
    role="dialog"
    tabindex="-1"
    id="ImageExportModal"
    aria-labelledby="imageExportTitle"
    aria-modal="true">
    <div
        class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable"
        role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="imageExportTitle">
                    <i class="bi bi-image" aria-hidden="true"></i> {m.image_export_title()}
                </h4>
                <button
                    type="button"
                    class="btn-close"
                    id="imageExportCloseBtn"
                    aria-label={m.image_export_close_label()}
                    onclick={hideModal}
                    disabled={exporting}></button>
            </div>
            <div class="modal-body">
                {#if exporting}
                    <div
                        class="alert alert-info"
                        role="status"
                        aria-live="polite">
                        <i
                            class="bi bi-exclamation-diamond-fill"
                            aria-hidden="true"></i>
                        {m.image_export_keep_open_warning()}
                    </div>
                {/if}

                <!-- Two Column Layout -->
                <div class="row">
                    <!-- Left Column - Export Settings -->
                    <div class="col-md-6">
                        <form onsubmit={preventDefault(exportImages)}>
                            <!-- Export Folder -->
                            <div
                                id="targetFolderInputGroup"
                                class="mb-3 p-3 border border-secondary rounded">
                                <p class="lead mb-2" id="exportLocationLabel">
                                    {m.image_export_location_label()}
                                </p>
                                <button
                                    type="button"
                                    id="chooseFolderBtn"
                                    disabled={exporting}
                                    class="btn btn-info text-white"
                                    onclick={selectTargetFolder}
                                    aria-labelledby="exportLocationLabel">
                                    <i class="bi bi-folder" aria-hidden="true"
                                    ></i> {m.image_export_choose_folder_button()}
                                </button>
                                <span
                                    class="ms-2 font-italic text-truncate"
                                    role="status">
                                    {targetFolderPath || m.image_export_no_folder_selected()}
                                </span>
                            </div>

                            <!-- Export Settings -->
                            <div class="row mb-3">
                                <div class="col-12">
                                    <label
                                        class="form-label"
                                        for="exportProfileSelect"
                                        >{m.image_export_profile_label()}</label>
                                    <select
                                        class="form-select"
                                        id="exportProfileSelect"
                                        bind:value={exportProfile}
                                        onchange={onProfileChange}
                                        disabled={exporting}>
                                        <option value="bdn"
                                            >{m.image_export_profile_bdn()}</option>
                                        <option value="scenarist"
                                            >{m.image_export_profile_scenarist()}</option>
                                    </select>
                                </div>
                            </div>

                            <div class="row mb-3">
                                <div class="col-md-6">
                                    <label
                                        class="form-label"
                                        for="eventGroupSelect"
                                        >{m.image_export_event_group_label()}</label>
                                    <select
                                        class="form-select"
                                        id="eventGroupSelect"
                                        bind:value={eventGroup}
                                        disabled={exporting}>
                                        {#each eventGroupState as eventGroupOption (eventGroupOption.id)}
                                            <option value={eventGroupOption.id}>
                                                {eventGroupOption.name}
                                            </option>
                                        {/each}
                                    </select>
                                </div>
                                <div class="col-md-6">
                                    <label
                                        class="form-label"
                                        for="resolutionSelect"
                                        >{m.image_export_resolution_label()}</label>
                                    <select
                                        class="form-select"
                                        id="resolutionSelect"
                                        bind:value={selectedResolution}
                                        disabled={exporting}>
                                        {#each availableResolutions as preset (preset.id)}
                                            <option value={preset.id}
                                                >{preset.name}</option>
                                        {/each}
                                    </select>
                                </div>
                            </div>

                            <div class="row mb-3">
                                <div class="col-md-6">
                                    <label
                                        class="form-label"
                                        for="scriptNameInput"
                                        >{m.image_export_script_name_label()}</label>
                                    <input
                                        type="text"
                                        class="form-control"
                                        id="scriptNameInput"
                                        bind:value={scriptName}
                                        placeholder={isScenarist
                                            ? m.image_export_script_name_placeholder_sst()
                                            : m.image_export_script_name_placeholder()}
                                        disabled={exporting} />
                                </div>
                                <div class="col-md-6">
                                    <label
                                        class="form-label"
                                        for="imagePrefixInput"
                                        >{m.image_export_image_prefix_label()}</label>
                                    <input
                                        type="text"
                                        class="form-control"
                                        id="imagePrefixInput"
                                        bind:value={imagePrefix}
                                        placeholder={m.image_export_image_prefix_placeholder()}
                                        disabled={exporting} />
                                </div>
                            </div>

                            <div class="row mb-3">
                                {#if !isScenarist}
                                    <div class="col-md-6">
                                        <label
                                            class="form-label"
                                            for="languageCodeInput"
                                            >{m.image_export_language_code_label()}</label>
                                        <input
                                            type="text"
                                            class="form-control"
                                            id="languageCodeInput"
                                            bind:value={languageCode}
                                            placeholder={m.image_export_language_code_placeholder()}
                                            disabled={exporting} />
                                    </div>
                                {/if}
                                <div class={isScenarist ? "col-12" : "col-md-6"}>
                                    <label
                                        class="form-label"
                                        for="descriptionInput"
                                        >{m.image_export_description_label()}</label>
                                    <input
                                        type="text"
                                        class="form-control"
                                        id="descriptionInput"
                                        bind:value={descriptionText}
                                        placeholder={m.image_export_description_placeholder()}
                                        disabled={exporting} />
                                </div>
                            </div>

                            <!-- Background Settings -->
                            <div class="mb-3 p-3 border rounded">
                                <h6 class="text-primary mb-2">
                                    {m.image_export_background_settings_title()}
                                </h6>
                                <div class="row align-items-center">
                                    <div class="col-md-6">
                                        <div class="form-check">
                                            <input
                                                class="form-check-input"
                                                type="checkbox"
                                                id="transparentBgCheck"
                                                bind:checked={
                                                    useTransparentBackground
                                                }
                                                disabled={exporting ||
                                                    isScenarist} />
                                            <label
                                                class="form-check-label"
                                                for="transparentBgCheck">
                                                {m.image_export_transparent_background_label()}
                                            </label>
                                        </div>
                                    </div>
                                    <div class="col-md-6">
                                        <label
                                            class="form-label small"
                                            for="bgColorInput"
                                            >{m.image_export_solid_color_label()}</label>
                                        <div class="input-group input-group-sm">
                                            <input
                                                type="color"
                                                class="form-control form-control-color"
                                                id="bgColorInput"
                                                bind:value={backgroundColor}
                                                disabled={exporting ||
                                                    useTransparentBackground ||
                                                    isScenarist} />
                                            <input
                                                type="text"
                                                class="form-control"
                                                bind:value={backgroundColor}
                                                disabled={exporting ||
                                                    useTransparentBackground ||
                                                    isScenarist} />
                                        </div>
                                    </div>
                                </div>
                                {#if isScenarist}
                                    <p class="small text-muted mb-0 mt-2">
                                        {m.image_export_scenarist_bg_hint()}
                                    </p>
                                {/if}
                            </div>

                            <!-- Style Panel Toggle -->
                            <div class="mb-3">
                                <button
                                    type="button"
                                    class="btn btn-outline-dark"
                                    onclick={() =>
                                        (showStylePanel = !showStylePanel)}
                                    disabled={exporting}>
                                    <i class="bi bi-palette" aria-hidden="true"
                                    ></i>
                                    {showStylePanel
                                        ? m.image_export_style_toggle_hide()
                                        : m.image_export_style_toggle_show()}
                                </button>
                            </div>

                            <!-- Style Panel -->
                            {#if showStylePanel}
                                <div
                                    class="border border-secondary rounded p-3 mb-3">
                                    <h6 class="text-primary">
                                        {m.image_export_style_title()}
                                    </h6>

                                    <!-- Font Settings -->
                                    <div class="row g-2 mb-3">
                                        <div class="col-md-4">
                                            <label
                                                class="form-label small"
                                                for="fontFamilySelect"
                                                >{m.video_export_font_family_label()}</label>
                                            <select
                                                id="fontFamilySelect"
                                                class="form-select form-select-sm"
                                                bind:value={
                                                    styleState.fontFamily
                                                }
                                                disabled={exporting}>
                                                {#each fontState as font (font.id)}
                                                    <option
                                                        value={font.name}
                                                        style="font-family: {font.name};"
                                                        >{font.name}</option>
                                                {/each}
                                                <option
                                                    style="font-family: Arial;"
                                                    >Arial</option>
                                                <option
                                                    style="font-family: Verdana;"
                                                    >Verdana</option>
                                                <option
                                                    style="font-family: Helvetica;"
                                                    >Helvetica</option>
                                                <option
                                                    style="font-family: Tahoma;"
                                                    >Tahoma</option>
                                                <option
                                                    style="font-family: Trebuchet MS;"
                                                    >Trebuchet MS</option>
                                                <option
                                                    style="font-family: Times New Roman;"
                                                    >Times New Roman</option>
                                                <option
                                                    style="font-family: Georgia;"
                                                    >Georgia</option>
                                                <option
                                                    style="font-family: Garamond;"
                                                    >Garamond</option>
                                                <option
                                                    style="font-family: Courier New;"
                                                    >Courier New</option>
                                                <option
                                                    style="font-family: Brush Script MT;"
                                                    >Brush Script MT</option>
                                                <option
                                                    style="font-family: serif;"
                                                    >serif</option>
                                                <option
                                                    style="font-family: sans-serif;"
                                                    >sans-serif</option>
                                                <option
                                                    style="font-family: plexMono;"
                                                    >plexMono</option>
                                                <option
                                                    style="font-family: cursive;"
                                                    >cursive</option>
                                                <option
                                                    style="font-family: fantasy;"
                                                    >fantasy</option>
                                            </select>
                                        </div>
                                        <div class="col-md-2">
                                            <label
                                                class="form-label small"
                                                for="fontSizeInput">{m.image_export_font_size_label()}</label>
                                            <input
                                                id="fontSizeInput"
                                                type="number"
                                                class="form-control form-control-sm"
                                                min="8"
                                                max="120"
                                                bind:value={styleState.fontSize}
                                                disabled={exporting} />
                                        </div>
                                        <div class="col-md-3">
                                            <label
                                                class="form-label small"
                                                for="fontColorInput"
                                                >{m.image_export_font_color_label()}</label>
                                            <input
                                                id="fontColorInput"
                                                type="color"
                                                class="form-control form-control-sm"
                                                bind:value={styleState.color}
                                                disabled={exporting} />
                                        </div>
                                        <div class="col-md-3">
                                            <label
                                                class="form-label small"
                                                for="opacityInput"
                                                >{m.video_export_opacity_label()}</label>
                                            <input
                                                id="opacityInput"
                                                type="number"
                                                class="form-control form-control-sm"
                                                min="0"
                                                max="100"
                                                bind:value={styleState.opacity}
                                                disabled={exporting} />
                                        </div>
                                    </div>

                                    <!-- Outline Settings -->
                                    <div class="row g-2 mb-3">
                                        <div class="col-md-3">
                                            <label
                                                class="form-label small"
                                                for="outlineColorInput"
                                                >{m.video_export_outline_color_label()}</label>
                                            <input
                                                id="outlineColorInput"
                                                type="color"
                                                class="form-control form-control-sm"
                                                bind:value={
                                                    styleState.outlineColor
                                                }
                                                disabled={exporting} />
                                        </div>
                                        <div class="col-md-3">
                                            <label
                                                class="form-label small"
                                                for="outlineOpacityInput"
                                                >{m.video_export_outline_opacity_label()}</label>
                                            <input
                                                id="outlineOpacityInput"
                                                type="number"
                                                class="form-control form-control-sm"
                                                min="0"
                                                max="100"
                                                bind:value={
                                                    styleState.outlineOpacity
                                                }
                                                disabled={exporting} />
                                        </div>
                                        <div class="col-md-3">
                                            <label
                                                class="form-label small"
                                                for="outlineSizeInput"
                                                >{m.video_export_outline_size_label()}</label>
                                            <input
                                                id="outlineSizeInput"
                                                type="number"
                                                class="form-control form-control-sm"
                                                min="0"
                                                step="0.5"
                                                bind:value={styleState.outline}
                                                disabled={exporting} />
                                        </div>
                                        <div class="col-md-3">
                                            <label
                                                class="form-label small"
                                                for="spacingInput"
                                                >{m.video_export_letter_spacing_label()}</label>
                                            <input
                                                id="spacingInput"
                                                type="number"
                                                class="form-control form-control-sm"
                                                min="0"
                                                bind:value={styleState.spacing}
                                                disabled={exporting} />
                                        </div>
                                    </div>

                                    <!-- Shadow Settings -->
                                    <div class="row g-2 mb-3">
                                        <div class="col-md-3">
                                            <label
                                                class="form-label small"
                                                for="shadowColorInput"
                                                >{m.video_export_shadow_color_label()}</label>
                                            <input
                                                id="shadowColorInput"
                                                type="color"
                                                class="form-control form-control-sm"
                                                bind:value={
                                                    styleState.shadowColor
                                                }
                                                disabled={exporting} />
                                        </div>
                                        <div class="col-md-3">
                                            <label
                                                class="form-label small"
                                                for="shadowOpacityInput"
                                                >{m.video_export_shadow_opacity_label()}</label>
                                            <input
                                                id="shadowOpacityInput"
                                                type="number"
                                                class="form-control form-control-sm"
                                                min="0"
                                                max="100"
                                                bind:value={
                                                    styleState.shadowOpacity
                                                }
                                                disabled={exporting} />
                                        </div>
                                        <div class="col-md-3">
                                            <label
                                                class="form-label small"
                                                for="shadowSizeInput"
                                                >{m.video_export_shadow_size_label()}</label>
                                            <input
                                                id="shadowSizeInput"
                                                type="number"
                                                class="form-control form-control-sm"
                                                min="0"
                                                step="0.5"
                                                bind:value={styleState.shadow}
                                                disabled={exporting} />
                                        </div>
                                        <div class="col-md-3">
                                            <label
                                                class="form-label small"
                                                for="borderStyleSelect"
                                                >{m.video_export_border_style_label()}</label>
                                            <select
                                                id="borderStyleSelect"
                                                class="form-select form-select-sm"
                                                bind:value={
                                                    styleState.borderStyle
                                                }
                                                disabled={exporting}>
                                                <option value="1">{m.video_export_border_style_outline_shadow()}</option>
                                                <option value="3">{m.video_export_border_style_solid_background()}</option>
                                            </select>
                                        </div>
                                    </div>

                                    <!-- Position Settings -->
                                    <div class="row g-2 mb-3">
                                        <div class="col-md-6">
                                            <label
                                                class="form-label small"
                                                for="xPaddingInput"
                                                >{m.video_export_horizontal_padding_label()}</label>
                                            <input
                                                id="xPaddingInput"
                                                type="number"
                                                class="form-control form-control-sm"
                                                min="0"
                                                max="50"
                                                step="0.5"
                                                bind:value={styleState.xPadding}
                                                disabled={exporting} />
                                        </div>
                                        <div class="col-md-6">
                                            <label
                                                class="form-label small"
                                                for="yPaddingInput"
                                                >{m.video_export_vertical_padding_label()}</label>
                                            <input
                                                id="yPaddingInput"
                                                type="number"
                                                class="form-control form-control-sm"
                                                min="0"
                                                max="50"
                                                step="0.5"
                                                bind:value={styleState.yPadding}
                                                disabled={exporting} />
                                        </div>
                                    </div>

                                    <!-- Preset Management -->
                                    <div class="row g-2 align-items-end">
                                        <div class="col-md-6">
                                            <label
                                                class="form-label small"
                                                for="stylePresetSelect"
                                                >{m.video_export_style_preset_label()}</label>
                                            <select
                                                id="stylePresetSelect"
                                                class="form-select form-select-sm"
                                                bind:value={
                                                    styleState.selectedPreset
                                                }
                                                onchange={() => selectPreset()}
                                                disabled={exporting}>
                                                {#each styleState.presets as preset (preset.id)}
                                                    <option value={preset}
                                                        >{preset.name}</option>
                                                {/each}
                                            </select>
                                        </div>
                                        <div class="col-md-3">
                                            <button
                                                type="button"
                                                class="btn btn-sm btn-success w-100"
                                                onclick={() => savePreset()}
                                                disabled={exporting}>
                                                <i
                                                    class="bi bi-floppy"
                                                    aria-hidden="true"></i> {m.image_export_style_save_button()}
                                            </button>
                                        </div>
                                        <div class="col-md-3">
                                            <button
                                                type="button"
                                                class="btn btn-sm btn-danger w-100"
                                                onclick={() => deletePreset()}
                                                disabled={!styleState.selectedPreset ||
                                                    exporting}>
                                                <i
                                                    class="bi bi-trash"
                                                    aria-hidden="true"></i> {m.image_export_style_delete_button()}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            {/if}
                        </form>
                    </div>

                    <!-- Right Column - Preview -->
                    <div class="col-md-6">
                        <div class="border border-secondary rounded p-3 h-100">
                            <h6 class="text-primary mb-3">{m.image_export_preview_title()}</h6>

                            <!-- Preview Controls -->
                            <div class="mb-3">
                                <div class="row g-1 align-items-end">
                                    <div class="col-1">
                                        <button
                                            type="button"
                                            class="btn btn-sm btn-outline-dark w-100"
                                            onclick={goToStart}
                                            disabled={generatingPreview ||
                                                exporting ||
                                                selectedEventIndex === 0}
                                            title={m.image_export_preview_first_title()}>
                                            <i
                                                class="bi bi-skip-start"
                                                aria-hidden="true"></i>
                                        </button>
                                    </div>
                                    <div class="col-1">
                                        <button
                                            type="button"
                                            class="btn btn-sm btn-outline-dark w-100"
                                            onclick={goToPrevious}
                                            disabled={generatingPreview ||
                                                exporting ||
                                                selectedEventIndex === 0}
                                            title={m.image_export_preview_previous_title()}>
                                            <i
                                                class="bi bi-skip-backward"
                                                aria-hidden="true"></i>
                                        </button>
                                    </div>
                                    <div class="col-1">
                                        <button
                                            type="button"
                                            class="btn btn-sm btn-outline-dark w-100"
                                            onclick={goToNext}
                                            disabled={generatingPreview ||
                                                exporting ||
                                                selectedEventIndex >=
                                                    totalEvents - 1}
                                            title={m.image_export_preview_next_title()}>
                                            <i
                                                class="bi bi-skip-forward"
                                                aria-hidden="true"></i>
                                        </button>
                                    </div>
                                    <div class="col-1">
                                        <button
                                            type="button"
                                            class="btn btn-sm btn-outline-dark w-100"
                                            onclick={goToEnd}
                                            disabled={generatingPreview ||
                                                exporting ||
                                                selectedEventIndex >=
                                                    totalEvents - 1}
                                            title={m.image_export_preview_last_title()}>
                                            <i
                                                class="bi bi-skip-end"
                                                aria-hidden="true"></i>
                                        </button>
                                    </div>
                                    <div class="col-4">
                                        <label
                                            class="form-label small"
                                            for="eventNumberInput">{m.image_export_preview_event_label()}</label>
                                        <input
                                            id="eventNumberInput"
                                            type="number"
                                            class="form-control form-control-sm"
                                            min="1"
                                            max={totalEvents}
                                            value={selectedEventIndex + 1}
                                            oninput={(e) =>
                                                (selectedEventIndex = Math.max(
                                                    0,
                                                    Math.min(
                                                        parseInt(
                                                            e.target.value,
                                                        ) - 1,
                                                        totalEvents - 1,
                                                    ),
                                                ))}
                                            disabled={exporting} />
                                    </div>
                                    <div class="col-4">
                                        <button
                                            type="button"
                                            class="btn btn-sm btn-primary w-100"
                                            onclick={generatePreview}
                                            disabled={generatingPreview ||
                                                exporting}>
                                            {#if generatingPreview}
                                                <i
                                                    class="bi bi-hourglass-split"
                                                    aria-hidden="true"></i> {m.image_export_preview_generating_label()}
                                            {:else}
                                                <i
                                                    class="bi bi-camera"
                                                    aria-hidden="true"></i> {m.image_export_preview_generate_button()}
                                            {/if}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <!-- Preview Image -->
                            <div
                                class="preview-container position-relative"
                                style="background: repeating-conic-gradient(#ccc 0% 25%, #fff 0% 50%) 50% / 20px 20px;">
                                {#if previewImageSrc}
                                    <img
                                        src={previewImageSrc}
                                        alt={m.image_export_preview_image_alt()}
                                        class="img-fluid border w-100"
                                        style="max-height: 280px; object-fit: contain;" />
                                    <button
                                        type="button"
                                        class="btn btn-sm btn-secondary position-absolute"
                                        style="top: 8px; right: 8px; opacity: 0.8;"
                                        onclick={() =>
                                            (showMagnifiedPreview = true)}
                                        title={m.image_export_preview_magnify_title()}>
                                        <i
                                            class="bi bi-zoom-in"
                                            aria-hidden="true"></i>
                                    </button>
                                {:else if generatingPreview}
                                    <div
                                        class="d-flex justify-content-center align-items-center bg-light text-dark"
                                        style="height: 200px;">
                                        <BarLoader
                                            size="60"
                                            color="#1eb4b2"
                                            unit="px"
                                            duration="2s"></BarLoader>
                                    </div>
                                {:else}
                                    <div
                                        class="bg-light text-dark d-flex align-items-center justify-content-center"
                                        style="height: 200px;">
                                        <span class="text-muted"
                                            >{m.image_export_preview_generate_hint()}</span>
                                    </div>
                                {/if}
                            </div>

                            <!-- Current Event Info -->
                            {#if eventGroupState.find((g) => g.id === eventGroup)?.events?.[selectedEventIndex]}
                                {@const currentEvent = eventGroupState.find(
                                    (g) => g.id === eventGroup,
                                ).events[selectedEventIndex]}
                                <div
                                    class="mt-3 p-2 bg-light text-dark rounded">
                                    <div
                                        class="text-wrap text-center paragraphWrapper"
                                        style="font-size: 0.875rem; max-height: 80px; overflow-y: auto;">
                                        {@html currentEvent.text ||
                                            `<p class="text-muted">${m.image_export_preview_empty_event()}</p>`}
                                    </div>
                                </div>
                            {/if}

                            <!-- Export Summary -->
                            <div
                                class="mt-3 p-2 border rounded bg-light text-dark">
                                <small class="text-muted">
                                    <strong>{m.image_export_summary_title()}:</strong><br />
                                    {isScenarist
                                        ? m.image_export_summary_line_one_scenarist(
                                              {
                                                  count: totalEvents,
                                                  width: currentResolution.width,
                                                  height: currentResolution.height,
                                              },
                                          )
                                        : m.image_export_summary_line_one({
                                              count: totalEvents,
                                              width: currentResolution.width,
                                              height: currentResolution.height,
                                          })}<br />
                                    {isScenarist
                                        ? m.image_export_summary_prefix_scenarist(
                                              {
                                                  prefix: imagePrefix,
                                                  lastIndex: String(
                                                      totalEvents,
                                                  ).padStart(4, "0"),
                                              },
                                          )
                                        : m.image_export_summary_prefix({
                                              prefix: imagePrefix,
                                              lastIndex: String(
                                                  totalEvents,
                                              ).padStart(4, "0"),
                                          })}
                                </small>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Progress Bar -->
                {#if exporting}
                    <div class="mt-3">
                        <div
                            class="progress"
                            role="progressbar"
                            aria-valuemin="0"
                            aria-valuemax="100"
                            aria-valuenow={progress}>
                            <div
                                class="progress-bar bg-primary progress-bar-striped progress-bar-animated"
                                style="width: {progress}%;">
                                {progress}%
                            </div>
                        </div>
                        <p
                            class="text-muted mt-2"
                            role="status"
                            aria-live="polite">
                            <i class="bi bi-info-circle" aria-hidden="true"></i>
                            {statusMsg}
                        </p>
                    </div>
                {/if}
            </div>

            <!-- Modal Footer -->
            <div class="modal-footer">
                <button
                    type="button"
                    class="btn btn-secondary"
                    onclick={hideModal}
                    disabled={exporting}>
                    {m.image_export_cancel_button()}
                </button>
                <button
                    type="button"
                    disabled={!targetFolderPath ||
                        exporting ||
                        totalEvents === 0}
                    class="btn btn-primary"
                    onclick={exportImages}>
                    <i class="bi bi-download" aria-hidden="true"></i> {m.image_export_export_button({ count: totalEvents })}
                </button>
            </div>
        </div>
    </div>
</div>

<!-- Magnified Preview Overlay -->
{#if showMagnifiedPreview && previewImageSrc}
    <!-- svelte-ignore a11y_click_events_have_key_events -->
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div
        class="modal show d-block"
        style="background-color: rgba(0,0,0,0.9); z-index: 1060;"
        onclick={() => (showMagnifiedPreview = false)}
        role="dialog"
        tabindex="-1"
        aria-label={m.image_export_magnified_aria()}>
        <!-- svelte-ignore a11y_no_static_element_interactions -->
        <div
            class="modal-dialog modal-xl modal-dialog-centered"
            onclick={(e) => e.stopPropagation()}>
            <div class="modal-content bg-transparent border-0">
                <div class="modal-header border-0 pb-0">
                    <h5 class="modal-title text-white">
                        {m.image_export_magnified_title()}
                    </h5>
                    <button
                        type="button"
                        class="btn-close btn-close-white"
                        onclick={() => (showMagnifiedPreview = false)}
                        aria-label={m.image_export_magnified_close_label()}></button>
                </div>
                <div
                    class="modal-body text-center p-0"
                    style="background: repeating-conic-gradient(#333 0% 25%, #444 0% 50%) 50% / 20px 20px;">
                    <img
                        src={previewImageSrc}
                        alt={m.image_export_magnified_image_alt()}
                        class="img-fluid rounded shadow-lg"
                        style="max-height: 75vh; max-width: 100%; object-fit: contain;" />
                </div>
                <div
                    class="modal-footer border-0 pt-0 justify-content-between align-items-center">
                    <small class="text-white-50">
                        {m.image_export_magnified_event_hint({
                            index: selectedEventIndex + 1,
                            total: totalEvents,
                            width: currentResolution.width,
                            height: currentResolution.height,
                        })}
                    </small>
                    <button
                        type="button"
                        class="btn btn-success btn-sm"
                        onclick={downloadPreview}
                        title={m.image_export_magnified_download_title()}>
                        <i class="bi bi-download" aria-hidden="true"></i> {m.image_export_magnified_download_button()}
                    </button>
                </div>
            </div>
        </div>
    </div>
{/if}

<style lang="css">
#targetFolderInputGroup {
    background-color: #00000014;
}

.preview-container:hover .btn {
    opacity: 1 !important;
}
</style>
